import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Alert,
  Chip,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import { Box } from "@mui/system";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import DealsAndPromotionsEditModal from "./DealsAndPromotionsEditModal";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  setCommissionPlansPage,
  setCommissionPlansRowsPerPage,
  setCommissionPlansSort,
  SortOrder,
} from "../../../features/dealsPromotionsSlice";

interface Props {
  tableHeadings: { [key: string]: string };
  rows: any[];
  canEdit: boolean;
}

function applySortAndPage(
  rows: any[],
  sortBy: string,
  sortOrder: SortOrder,
  page: number,
  rowsPerPage: number,
) {
  let sorted = [...rows];
  if (sortBy) {
    sorted.sort((a, b) => {
      const aVal = a[sortBy] ?? "";
      const bVal = b[sortBy] ?? "";
      const cmp =
        typeof aVal === "number" && typeof bVal === "number"
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal), undefined, {
              numeric: true,
              sensitivity: "base",
            });
      return sortOrder === "asc" ? cmp : -cmp;
    });
  }
  return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
}

const DealsAndPromotionsTable = ({ tableHeadings, rows, canEdit }: Props) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { sortBy, sortOrder, page, rowsPerPage, searchQuery, filters } =
    useSelector((state: any) => state.dealsPromotions.commissionPlans);
  const lastUpdatedPlanId = useSelector(
    (state: any) => state.dealsPromotions.lastUpdatedPlanId,
  );
  const currencies = useSelector(
    (state: any) => state.dealsPromotions.currencies,
  );
  const currencyOptions = React.useMemo(
    () =>
      (currencies ?? []).map((c: any) => ({
        label: c.currency_code,
        uid: c.shared_uid,
        symbol: c.currency_symbol ?? "",
      })),
    [currencies],
  );

  // Initialise page from URL on mount
  React.useEffect(() => {
    const urlPage = parseInt(searchParams.get("page") ?? "0", 10);
    if (!isNaN(urlPage) && urlPage !== page) {
      dispatch(setCommissionPlansPage(urlPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headCells = Object.entries(tableHeadings);

  const filteredRows = rows.filter((row) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesBrand = row.brand_name?.toLowerCase().includes(q);
      const matchesPlanName = row.plan_name?.toLowerCase().includes(q);
      const matchesPlanTerm = row.plan_term?.toLowerCase().includes(q);
      if (!matchesBrand && !matchesPlanName && !matchesPlanTerm) return false;
    }
    if (
      filters.brand_id.length > 0 &&
      !filters.brand_id.includes(String(row.brand_id))
    )
      return false;
    if (filters.website.length > 0 && !filters.website.includes(row.website))
      return false;
    if (filters.status.length > 0 && !filters.status.includes(row.status))
      return false;
    if (
      filters.plan_name.length > 0 &&
      !filters.plan_name.includes(row.plan_name)
    )
      return false;
    if (
      filters.plan_term.length > 0 &&
      !filters.plan_term.includes(row.plan_term)
    )
      return false;
    return true;
  });

  const visibleRows = applySortAndPage(
    filteredRows,
    sortBy,
    sortOrder,
    page,
    rowsPerPage,
  );

  const handleSort = (key: string) => {
    const newOrder: SortOrder =
      sortBy === key && sortOrder === "asc" ? "desc" : "asc";
    dispatch(setCommissionPlansSort({ sortBy: key, sortOrder: newOrder }));
  };

  const handleChangePage = (_: any, newPage: number) => {
    dispatch(setCommissionPlansPage(newPage));
    setSearchParams(
      (prev) => {
        prev.set("page", String(newPage));
        return prev;
      },
      { replace: true },
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setCommissionPlansRowsPerPage(+event.target.value));
    setSearchParams(
      (prev) => {
        prev.set("page", "0");
        return prev;
      },
      { replace: true },
    );
  };

  return (
    <>
      <Paper sx={{ boxShadow: "none" }}>
        <TableContainer className="">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {headCells.map(([key, value], index) => (
                  <TableCell key={index} align="left">
                    <TableSortLabel
                      active={sortBy === key}
                      direction={sortBy === key ? sortOrder : "asc"}
                      onClick={() => handleSort(key)}
                    >
                      {value && typeof value === "string" ? value : ""}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={headCells.length}
                    align="center"
                    sx={{ py: 6, color: "rgba(0,0,0,0.4)", fontSize: "14px" }}
                  >
                    No data found
                  </TableCell>
                </TableRow>
              )}
              {visibleRows.map((row, rowIndex) => (
                <React.Fragment key={row.id ?? rowIndex}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                      ...(lastUpdatedPlanId === row.id && {
                        animation: "rowHighlight 2s ease-out",
                        "@keyframes rowHighlight": {
                          "0%": { backgroundColor: "rgba(156, 39, 176, 0.15)" },
                          "100%": { backgroundColor: "transparent" },
                        },
                      }),
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {key === "brand_name" ? (
                          <Box display={"flex"} alignItems={"center"}>
                            <DealsAndPromotionsEditModal
                              row={row}
                              currencyOptions={currencyOptions}
                              disabled={!canEdit}
                            />
                            <b>{row[key] ? row[key] : "N/A"}</b>
                          </Box>
                        ) : key === "brand_id" ? (
                          <>{row[key] ? row[key] : "N/A"}</>
                        ) : key === "website" ? (
                          <div className="chip-data">
                            <Chip
                              label={row[key] ? row[key] : "N/A"}
                              sx={{
                                backgroundColor: "#9c27b0",
                                color: "#ffffff",
                                height: "24px",
                                fontWeight: "500",
                              }}
                            />
                          </div>
                        ) : key === "status" ? (
                          <div className="AlertItems" style={{ overflow: "hidden" }}>
                            {row[key] === "Complete" ? (
                              <Alert
                                icon={
                                  <CheckCircleOutlinedIcon
                                    fontSize="inherit"
                                    sx={{
                                      color: "rgba(30, 70, 32, 1)",
                                      fontSize: "18px",
                                    }}
                                  />
                                }
                                severity="success"
                                sx={{
                                  border: "1px solid rgba(46, 125, 50, 0.5)",
                                  padding: "0px 5px",
                                  display: "inline-flex",
                                  fontSize: "13px",
                                  width: "fit-content",
                                  alignItems: "center",
                                }}
                              >
                                {row[key]}
                              </Alert>
                            ) : row[key] === "Partial" ? (
                              <Alert
                                icon={
                                  <ErrorOutlineOutlinedIcon
                                    fontSize="inherit"
                                    sx={{
                                      color: "#ED6C02",
                                      fontSize: "18px",
                                    }}
                                  />
                                }
                                severity="info"
                                sx={{
                                  border: "1px solid #ED6C0280",
                                  padding: "0px 5px",
                                  display: "inline-flex",
                                  background: "#FFF4E5",
                                  color: "#ED6C02",
                                  fontSize: "13px",
                                  width: "fit-content",
                                  alignItems: "center",
                                }}
                              >
                                {row[key]}
                              </Alert>
                            ) : (
                              "N/A"
                            )}
                          </div>
                        ) : key === "plan_name" ? (
                          <>
                            {row[key] ? (
                              <Tooltip title={row[key]} placement="top" arrow>
                                <span>
                                  {row[key].length > 40
                                    ? `${row[key].slice(0, 40)}…`
                                    : row[key]}
                                </span>
                              </Tooltip>
                            ) : (
                              "N/A"
                            )}
                          </>
                        ) : key === "plan_term" ? (
                          <>{row[key] ? row[key] : "N/A"}</>
                        ) : key === "cpa" ? (
                          <>
                            {row[key] != null && row[key] !== "" ? (
                              <span>
                                {row[key]} {row.currency_symbol}
                              </span>
                            ) : (
                              <span
                                style={{ color: "#d32f2f", fontWeight: 500 }}
                              >
                                —
                              </span>
                            )}
                          </>
                        ) : key === "rs" ? (
                          <>
                            {row[key] ? (
                              row[key]
                            ) : (
                              <span
                                style={{ color: "#d32f2f", fontWeight: 500 }}
                              >
                                —
                              </span>
                            )}
                          </>
                        ) : key === "currency" ? (
                          <>
                            {row[key] ? (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                {row[key]}
                                <span style={{ color: "rgba(0,0,0,0.56)" }}>
                                  {row.currency_symbol}
                                </span>
                              </Box>
                            ) : (
                              <span
                                style={{ color: "#d32f2f", fontWeight: 500 }}
                              >
                                —
                              </span>
                            )}
                          </>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            sx={{ position: "relative" }}
            alignItems="center"
          >
            {/* Left: rows-per-page selector + "X–Y of Z" count */}
            <Stack direction="row" alignItems="center">
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={filteredRows?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                classes={{
                  displayedRows: "hidden-displayed-rows",
                }}
                sx={{
                  ".MuiTablePagination-actions": {
                    display: "none",
                  },
                  ".MuiTablePagination-input": {
                    marginRight: "15px",
                  },
                }}
              />
              <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={filteredRows?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },
                  ".MuiTablePagination-actions": {
                    display: "none",
                  },
                  ".MuiTablePagination-toolbar": {
                    paddingLeft: "0px",
                  },
                }}
              />
            </Stack>

            {/* Center: page number buttons */}
            <Pagination
              count={Math.ceil(filteredRows?.length / rowsPerPage)}
              page={page + 1}
              showFirstButton
              showLastButton
              className="pagination-button"
              color="secondary"
              size="small"
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                ".css-wjh20t-MuiPagination-ul": {
                  justifyContent: "center",
                },
              }}
              onChange={(_, value) => handleChangePage(null, value - 1)}
            />
            {/* Right spacer to balance the left side */}
            <Box />
          </Stack>
        </TableContainer>
      </Paper>
    </>
  );
};

export default DealsAndPromotionsTable;
